<template>
  <!-- Группа-аккордеон: у пункта есть подпункты -->
  <div v-if="item.children?.length" class="nav-group">
    <component
      :is="item.path ? RouterLink : 'button'"
      v-bind="item.path ? { to: item.path } : { type: 'button' }"
      :id="linkId"
      class="nav-item"
      :class="{ 'nav-item--active': isActive(item.path), 'nav-item--collapsed': collapsed }"
      v-tooltip="collapsed ? item.label : undefined"
      @click="handleGroupClick"
    >
      <span class="nav-item-icon">
        <component :is="item.icon ?? HomeIcon" :size="19" />
        <span v-if="showAlertDot" class="nav-item-dot" aria-hidden="true" />
      </span>
      <Transition name="slide-fade">
        <span v-if="!collapsed" class="nav-item-label">{{ item.label }}</span>
      </Transition>
      <Transition name="fade">
        <span
          v-if="!collapsed && groupBadge"
          class="nav-item-badge"
          :class="{ 'nav-item-badge--alert': groupAlert }"
          >{{ groupBadge }}</span
        >
      </Transition>
      <span
        v-if="!collapsed"
        class="nav-item-chevron"
        :class="{ 'nav-item-chevron--open': expanded }"
      >
        <ChevronRightIcon :size="16" />
      </span>
    </component>

    <div v-if="!collapsed" class="nav-children" :class="{ 'nav-children--open': expanded }">
      <router-link
        v-for="child in item.children"
        :key="child.path"
        :to="child.path!"
        class="nav-item nav-item--child"
        :class="{ 'nav-item--active': isActive(child.path) }"
      >
        <span class="nav-item-icon">
          <component :is="child.icon ?? HomeIcon" :size="17" />
        </span>
        <span class="nav-item-label">{{ child.label }}</span>
        <span
          v-if="child.badge"
          class="nav-item-badge"
          :class="{ 'nav-item-badge--alert': child.badgeVariant === 'alert' }"
          >{{ child.badge }}</span
        >
      </router-link>
    </div>
  </div>

  <!-- Обычный пункт -->
  <router-link
    v-else
    :to="item.path!"
    :id="linkId"
    class="nav-item"
    :class="{ 'nav-item--active': isActive(item.path), 'nav-item--collapsed': collapsed }"
    v-tooltip="collapsed ? item.label : undefined"
    @click="emit('navigate')"
  >
    <span class="nav-item-icon">
      <component :is="item.icon ?? HomeIcon" :size="19" />
      <span v-if="showAlertDot" class="nav-item-dot" aria-hidden="true" />
    </span>
    <Transition name="slide-fade">
      <span v-if="!collapsed" class="nav-item-label">{{ item.label }}</span>
    </Transition>
    <Transition name="fade">
      <span
        v-if="!collapsed && item.badge"
        class="nav-item-badge"
        :class="{ 'nav-item-badge--alert': item.badgeVariant === 'alert' }"
        >{{ item.badge }}</span
      >
    </Transition>
  </router-link>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { RouterLink } from 'vue-router'
import { HomeIcon, ChevronRightIcon } from 'lucide-vue-next'
import type { NavItem } from './sidebarNav'

const props = defineProps<{
  item: NavItem
  collapsed?: boolean
  /** Самый специфичный маршрут меню для текущей страницы — см. resolveActivePath. */
  activePath?: string
  /** Нужен подсказке про язык, которая цепляется за пункт «Настройки». */
  linkId?: string
}>()

const emit = defineEmits<{
  /** Свёрнутый сайдбар не показывает подпункты — просим родителя развернуться. */
  'expand-sidebar': []
  navigate: []
}>()

const isActive = (path?: string): boolean => !!path && path === props.activePath

const manualOpen = ref(false)
const hasActiveChild = computed(() => !!props.item.children?.some((c) => isActive(c.path)))
const expanded = computed(() => manualOpen.value || hasActiveChild.value)

// Пока группа закрыта, её бейдж суммирует счётчики подпунктов — иначе
// «требуют действия» остаётся незамеченным под свёрнутым аккордеоном.
const groupBadge = computed<string | undefined>(() => {
  if (expanded.value) return undefined
  const sum = (props.item.children ?? []).reduce((acc, c) => acc + (Number(c.badge) || 0), 0)
  return sum > 0 ? String(sum) : undefined
})

// Алерт подпункта поднимается на закрытую группу: иначе просрочка прячется под аккордеоном.
const groupAlert = computed<boolean>(
  () => !expanded.value && !!props.item.children?.some((c) => c.badgeVariant === 'alert'),
)

// В свёрнутом сайдбаре бейджей нет — на алерт остаётся точка поверх иконки (DEV-1165).
const showAlertDot = computed<boolean>(
  () => !!props.collapsed && (props.item.badgeVariant === 'alert' || groupAlert.value),
)

const handleGroupClick = (): void => {
  emit('navigate')
  if (props.collapsed) {
    emit('expand-sidebar')
    manualOpen.value = true
    return
  }
  manualOpen.value = !expanded.value
}
</script>

<style scoped>
.nav-group {
  display: flex;
  flex-direction: column;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 11px;
  width: 100%;
  height: 42px;
  padding: 0 10px 0 12px;
  border: none;
  background: transparent;
  border-radius: var(--radius-md, 10px);
  color: var(--color-text-secondary);
  text-decoration: none;
  font-family: var(--font-family-sans);
  font-size: 0.875rem; /* 14px */
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease;
  position: relative;
}

.nav-item:hover {
  background: var(--color-bg-muted);
  color: var(--color-text-primary);
}

.nav-item--active {
  background: var(--color-primary-tint);
  color: var(--color-primary-hover);
}

.nav-item--active .nav-item-label {
  font-weight: 600;
}

/* Дефолтное UA-кольцо фокуса торчит за скругление пилла и выглядит сломанным —
   рисуем контур внутри, по радиусу пункта. */
.nav-item:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: -2px;
}

.nav-item--collapsed {
  justify-content: center;
  padding: 0;
}

.nav-item-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 19px;
  height: 19px;
  position: relative;
}

/* Свёрнутый сайдбар прячет бейджи — про просрочку сообщает точка на иконке (DEV-1165). */
.nav-item-dot {
  position: absolute;
  top: -2px;
  right: -3px;
  width: 8px;
  height: 8px;
  border-radius: var(--radius-full, 9999px);
  background: var(--color-danger);
  box-shadow: 0 0 0 2px var(--color-surface);
}

.nav-item-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.nav-item-badge {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  background: var(--color-primary-tint);
  color: var(--color-primary);
  font-size: 0.71875rem; /* 11.5px */
  font-weight: 600;
  border-radius: var(--radius-full, 9999px);
}

.nav-item-badge--alert {
  background: var(--color-danger);
  color: var(--color-text-inverse);
}

.nav-item-chevron {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  color: var(--color-text-tertiary, var(--color-text-secondary));
  transition: transform 0.2s ease;
}

.nav-item-chevron--open {
  transform: rotate(90deg);
}

.nav-children {
  display: flex;
  flex-direction: column;
  gap: 2px;
  overflow: hidden;
  max-height: 0;
  opacity: 0;
  transition:
    max-height 0.25s ease,
    opacity 0.2s ease;
}

.nav-children--open {
  max-height: 320px; /* с запасом под несколько подпунктов */
  opacity: 1;
}

.nav-item--child {
  height: 38px;
  /* Отступ мельче парного 36px: длинная подпись («Персональные настройки»)
     в активном — полужирном — состоянии в 36px уже не влезала и обрезалась. */
  padding-left: 28px;
  font-size: 0.84375rem; /* 13.5px — подпункт мельче родителя */
}

.nav-item--child .nav-item-icon {
  width: 17px;
  height: 17px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(-10px);
  opacity: 0;
}
</style>
